import React, { useEffect, useState } from 'react';
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const emptyGuid = '00000000-0000-0000-0000-000000000000';

const initialFieldValues = {
    brandId: emptyGuid,
    brandName: ''
};

const Brand = () => {
    const [lists, setLists] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [btnLoading, setBtnLoading] = useState(false);

    const THEME_COLOR = '#065f46';

    // ================= GET =================
    const GetData = async () => {
        try {
            const res = await axios.get(
                config.APIACTIVEURL + config.GETALLBRANDS
            );
            setLists(res.data || []);
        } catch (error) {
            console.error("Error fetching brands", error);
        }
    };

    useEffect(() => {
        GetData();
    }, []);

    // ================= FORM =================
    const handleInput = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const validate = () => {
        let temp = {};
        temp.brandName = values.brandName ? "" : "Brand name is required";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    // ================= ADD / UPDATE =================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setBtnLoading(true);
        try {
            const isNew = values.brandId === emptyGuid;

            if (isNew) {
                await axios.post(
                    config.APIACTIVEURL + config.CREATEBRAND,
                    values
                );
            } else {
                await axios.put(
                    config.APIACTIVEURL + config.UPDATEBRAND,
                    values
                );
            }

            setShowModal(false);
            resetForm();
            GetData();
        } catch (error) {
            console.error("Save failed", error);
        } finally {
            setBtnLoading(false);
        }
    };

    // ================= EDIT =================
    const handleEdit = (item) => {
        setValues({
            brandId: item.brandId,
            brandName: item.brandName
        });
        setErrors({});
        setShowModal(true);
    };

    // ================= DELETE =================
    const handleDelete = async () => {
        try {
            await axios.delete(
                `${config.APIACTIVEURL}${config.DELETEBRAND}/${itemToDelete}`
            );
            setShowDeleteModal(false);
            GetData();
        } catch (error) {
            console.error("Delete failed", error);
        }
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
    };

    // ================= UI =================
    return (
        <div className="page-content" style={{ paddingTop: "80px" }}>
            <div className="container-fluid py-4">
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <h4 className="fw-bold mb-0" style={{ color: '#064e3b' }}>
                            Brands
                        </h4>
                        <button
                            className="btn text-white fw-bold px-4 shadow-sm"
                            style={{ backgroundColor: THEME_COLOR }}
                            onClick={() => { resetForm(); setShowModal(true); }}
                        >
                            <i className="ri-add-line me-1"></i> Add Brand
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr className="text-secondary small text-uppercase">
                                        <th>Brand Name</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lists.map((item) => (
                                        <tr key={item.brandId}>
                                            <td className="fw-bold text-dark">
                                                {item.brandName}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-outline-primary border-0 me-2"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    <i className="ri-pencil-line fs-5"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger border-0"
                                                    onClick={() => {
                                                        setItemToDelete(item.brandId);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <i className="ri-delete-bin-line fs-5"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {lists.length === 0 && (
                                        <tr>
                                            <td colSpan="2" className="text-center text-muted py-4">
                                                No brands found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ADD / EDIT MODAL */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold">
                            {values.brandId === emptyGuid ? "Add Brand" : "Edit Brand"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="px-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted">
                                    Brand Name
                                </label>
                                <input
                                    type="text"
                                    name="brandName"
                                    value={values.brandName}
                                    onChange={handleInput}
                                    className={`form-control ${errors.brandName ? 'is-invalid' : ''}`}
                                    placeholder="e.g. L'Oréal, Matrix, Nivea"
                                />
                                {errors.brandName &&
                                    <div className="invalid-feedback">{errors.brandName}</div>}
                            </div>

                            <div className="modal-footer border-0 p-0 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-light px-4 fw-bold"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={btnLoading}
                                    className="btn text-white px-5 fw-bold shadow-sm"
                                    style={{ backgroundColor: THEME_COLOR }}
                                >
                                    {btnLoading ? 'Saving...' : 'Save Brand'}
                                </button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* DELETE MODAL */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Brand</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this brand?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default Brand;
